"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { RefreshCcw } from "lucide-react";
import { type Dispatch, type PropsWithChildren, type SetStateAction, createContext, useContext, useEffect, useRef, useState } from "react";
import { FormattedTime } from "@defuse/ui/formatted-time";
import { type TransactionDetails, getQuoteEvents } from "@/lib/transactions/api/getTransactionByDepositAddress";
type TimestampsVisibleContext = [boolean, Dispatch<SetStateAction<boolean>>];
const TimestampsVisibleContext = createContext<TimestampsVisibleContext>([false, () => {
  throw new Error("setIsTimestampsVisible not initialized");
}]);
export function TimestampsVisibleProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const t1 = useState(false);
  let t2;
  if ($[0] !== children || $[1] !== t1) {
    t2 = <TimestampsVisibleContext.Provider value={t1}>{children}</TimestampsVisibleContext.Provider>;
    $[0] = children;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function useTimestampsVisible() {
  return useContext(TimestampsVisibleContext);
}
export function SwapStepTimestamp(t0) {
  const $ = _c(35);
  const {
    quote
  } = t0;
  const {
    depositAddress,
    depositMemo
  } = quote;
  const [isTimestampsVisible, setIsTimestampsVisible] = useTimestampsVisible();
  const prevStatusRef = useRef(quote.status);
  let t1;
  let t2;
  if ($[0] !== quote.status || $[1] !== setIsTimestampsVisible) {
    t1 = () => {
      if (prevStatusRef.current !== quote.status) {
        prevStatusRef.current = quote.status;
        setIsTimestampsVisible(false);
      }
    };
    t2 = [quote.status, setIsTimestampsVisible];
    $[0] = quote.status;
    $[1] = setIsTimestampsVisible;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== depositAddress || $[5] !== quote.depositMemo || $[6] !== quote.status) {
    t3 = ["quoteEvents", depositAddress, quote.depositMemo, quote.status];
    $[4] = depositAddress;
    $[5] = quote.depositMemo;
    $[6] = quote.status;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== depositAddress || $[9] !== depositMemo) {
    t4 = () => getQuoteEvents({
      depositAddress,
      depositMemo
    });
    $[8] = depositAddress;
    $[9] = depositMemo;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== isTimestampsVisible || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: t4,
      enabled: isTimestampsVisible
    };
    $[11] = isTimestampsVisible;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    data: quoteEvents,
    isLoading,
    error,
    refetch
  } = useQuery(t5);
  let t6;
  if ($[15] !== quote.status || $[16] !== quoteEvents) {
    t6 = quoteEvents?.findLast(event => event.status === quote.status);
    $[15] = quote.status;
    $[16] = quoteEvents;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const lastEvent = t6;
  if (lastEvent !== undefined) {
    let t7;
    if ($[18] !== lastEvent.timestamp) {
      t7 = <>{" "}<FormattedTime date={lastEvent.timestamp} /></>;
      $[18] = lastEvent.timestamp;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    return t7;
  }
  if (isTimestampsVisible) {
    if (isLoading) {
      let t7;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <span className="whitespace-nowrap rounded-md border border-gray-300/70 bg-white/70 px-2 py-0.5 text-foreground/90 text-sm transition-colors hover:bg-white/80 dark:border-gray-600/70 dark:bg-background/60 dark:text-foreground inline-flex animate-pulse items-center gap-2" aria-live="polite">Loading...</span>;
        $[20] = t7;
      } else {
        t7 = $[20];
      }
      return t7;
    }
    if (error !== null) {
      let t7;
      if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <span>Error loading timestamp</span>;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      let t8;
      if ($[22] !== refetch) {
        t8 = () => {
          refetch();
        };
        $[22] = refetch;
        $[23] = t8;
      } else {
        t8 = $[23];
      }
      let t9;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <RefreshCcw className="h-3.5 w-3.5" />;
        $[24] = t9;
      } else {
        t9 = $[24];
      }
      let t10;
      if ($[25] !== t8) {
        t10 = <span className="text-muted-foreground ml-1 inline-flex items-center gap-1 text-sm">{t7}<button type="button" onClick={t8} className="hover:text-foreground inline-flex items-center" aria-label="Retry loading timestamp">{t9}</button></span>;
        $[25] = t8;
        $[26] = t10;
      } else {
        t10 = $[26];
      }
      return t10;
    }
    if (quoteEvents !== undefined && quoteEvents.length > 0) {
      let t7;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <span>No timestamp found</span>;
        $[27] = t7;
      } else {
        t7 = $[27];
      }
      let t8;
      if ($[28] !== refetch) {
        t8 = () => {
          refetch();
        };
        $[28] = refetch;
        $[29] = t8;
      } else {
        t8 = $[29];
      }
      let t9;
      if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <RefreshCcw className="h-3.5 w-3.5" />;
        $[30] = t9;
      } else {
        t9 = $[30];
      }
      let t10;
      if ($[31] !== t8) {
        t10 = <span className="text-muted-foreground ml-1 inline-flex items-center gap-1 text-sm">{t7}<button type="button" onClick={t8} className="hover:text-foreground inline-flex items-center" aria-label="Retry loading timestamp">{t9}</button></span>;
        $[31] = t8;
        $[32] = t10;
      } else {
        t10 = $[32];
      }
      return t10;
    }
    return null;
  }
  let t7;
  if ($[33] !== setIsTimestampsVisible) {
    t7 = <button type="button" onClick={() => {
      setIsTimestampsVisible(true);
    }} className="whitespace-nowrap rounded-md border border-gray-300/70 bg-white/70 px-2 py-0.5 text-foreground/90 text-sm transition-colors hover:bg-white/80 dark:border-gray-600/70 dark:bg-background/60 dark:text-foreground">Load timestamp</button>;
    $[33] = setIsTimestampsVisible;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  return t7;
}